import type { ScalarGrid } from '../types';
import { toLatticeFrame } from '../data/horizontalAxes';

/**
 * A triangle mesh in the camera's normalised cube: x and y across [-1, 1], z across [0, 1].
 *
 * Flat typed arrays rather than objects per vertex, in exactly the layout a GPU vertex buffer
 * wants - so the same mesh can be rasterised in software or uploaded, with no repacking. Indices
 * rather than expanded triangles for the same reason: a shared vertex is shaded once.
 */
export type SurfaceMesh = {
  /** 3 floats per vertex. */
  positions: Float32Array;
  /** 3 floats per vertex, unit length. */
  normals: Float32Array;
  /** 1 float per vertex: the field's own value, for the colour ramp. */
  values: Float32Array;
  /** 3 indices per triangle. */
  indices: Uint32Array;
  /**
   * Which vertices stand at the same position, as one group id per vertex.
   *
   * `null` when every vertex is its own position, which is every welded mesh - the lattice and the
   * refined triangulation both place one vertex per point, and `collapseCoincident` runs before
   * either. `toFacetedMesh` unwelds on purpose and fills this in, because the index it copied each
   * corner from is exactly the group.
   *
   * Here rather than recovered by the renderer, because recovering it means comparing positions,
   * and the only cheap way to compare three floats is to build a string from them. That fold cost
   * 26 ms per frame at the adaptive budget, and it was rebuilding an answer the unwelding already
   * had.
   */
  sharedVertices: Uint32Array | null;
  vertexCount: number;
  triangleCount: number;
};

export const EMPTY_MESH: SurfaceMesh = {
  positions: new Float32Array(0),
  normals: new Float32Array(0),
  values: new Float32Array(0),
  indices: new Uint32Array(0),
  sharedVertices: null,
  vertexCount: 0,
  triangleCount: 0,
};

/** A vertex before packing: grid-space position, plus the field value there. */
export type MeshVertex = { x: number; y: number; value: number };

/**
 * Computes a normal per vertex by area-weighted average of the triangles meeting at it.
 *
 * Unnormalised cross products are summed, so the magnitude - which is proportional to twice the
 * triangle's area - weights each contribution by size. That matters much more on an adaptive mesh
 * than a uniform one: a refined region has small triangles beside large ones, and averaging unit
 * normals would let a sliver count as much as the broad facet next to it.
 */
const computeNormals = (
  positions: Float32Array,
  indices: Uint32Array,
  vertexCount: number
): Float32Array => {
  const normals = new Float32Array(vertexCount * 3);

  for (let triangle = 0; triangle < indices.length; triangle += 3) {
    const [a, b, c] = [
      indices[triangle] * 3,
      indices[triangle + 1] * 3,
      indices[triangle + 2] * 3,
    ];

    const e1x = positions[b] - positions[a];
    const e1y = positions[b + 1] - positions[a + 1];
    const e1z = positions[b + 2] - positions[a + 2];
    const e2x = positions[c] - positions[a];
    const e2y = positions[c + 1] - positions[a + 1];
    const e2z = positions[c + 2] - positions[a + 2];

    let nx = e1y * e2z - e1z * e2y;
    let ny = e1z * e2x - e1x * e2z;
    let nz = e1x * e2y - e1y * e2x;

    /* Wound to face away from the plane regardless of the triangulation's orientation, so a
       Delaunay output that hands back mixed winding cannot punch dark holes in the surface. */
    if (nz < 0) {
      nx = -nx;
      ny = -ny;
      nz = -nz;
    }

    for (const vertex of [a, b, c]) {
      normals[vertex] += nx;
      normals[vertex + 1] += ny;
      normals[vertex + 2] += nz;
    }
  }

  for (let offset = 0; offset < normals.length; offset += 3) {
    const length = Math.hypot(
      normals[offset],
      normals[offset + 1],
      normals[offset + 2]
    );
    if (length === 0) {
      /* An orphan vertex, in no triangle. Faced upward so it cannot produce a NaN normal if it
         is ever referenced. */
      normals[offset + 2] = 1;
      continue;
    }
    normals[offset] /= length;
    normals[offset + 1] /= length;
    normals[offset + 2] /= length;
  }

  return normals;
};

/**
 * Triangle indices with every triangle wound counter-clockwise seen from above, which both
 * renderers read as the front. A triangle with no area keeps the order it came in.
 */
export const toCounterClockwise = (
  vertices: MeshVertex[],
  indices: number[]
): number[] =>
  Array.from({ length: Math.floor(indices.length / 3) }, (_, triangle) => {
    const [a, b, c] = indices.slice(triangle * 3, triangle * 3 + 3);
    const [pa, pb, pc] = [vertices[a], vertices[b], vertices[c]];
    const area = (pb.x - pa.x) * (pc.y - pa.y) - (pc.x - pa.x) * (pb.y - pa.y);
    return area < 0 ? [a, c, b] : [a, b, c];
  }).flat();

export type PackMeshArgs = {
  vertices: MeshVertex[];
  /** Triangle indices into `vertices`. */
  indices: number[];
  grid: ScalarGrid;
  heightScale: number;
};

/**
 * Packs vertices and indices into the buffer layout, normalising positions into the camera's cube.
 *
 * Positions keep z in [0, 1] and leave `heightScale` to the camera, which is what every projector
 * here already applies - baking it in as well would apply it twice. The normals do need it, because
 * the same field exaggerated twice as tall has genuinely steeper slopes, so they are computed
 * against a scaled copy - and that copy is the only thing here the scale touches, which is what
 * {@link rescaleNormals} exists to exploit.
 */
export const packMesh = ({
  vertices,
  indices,
  grid,
  heightScale,
}: PackMeshArgs): SurfaceMesh => {
  const vertexCount = vertices.length;
  if (vertexCount === 0 || indices.length === 0) return EMPTY_MESH;

  const positions = new Float32Array(vertexCount * 3);
  const values = new Float32Array(vertexCount);
  const [zMin, zMax] = grid.zRange;
  const zSpan = zMax - zMin || 1;
  const { toCubeX, toCubeY } = toLatticeFrame(grid);

  vertices.forEach(({ x, y, value }, index) => {
    positions[index * 3] = toCubeX(x);
    positions[index * 3 + 1] = toCubeY(y);
    positions[index * 3 + 2] = (value - zMin) / zSpan;
    values[index] = value;
  });

  const indexArray = new Uint32Array(toCounterClockwise(vertices, indices));

  const scaledForNormals = Float32Array.from(positions);
  for (let offset = 2; offset < scaledForNormals.length; offset += 3) {
    scaledForNormals[offset] *= heightScale;
  }

  return {
    positions,
    normals: computeNormals(scaledForNormals, indexArray, vertexCount),
    values,
    indices: indexArray,
    sharedVertices: null,
    vertexCount,
    triangleCount: indexArray.length / 3,
  };
};

/**
 * A mesh with its normals recomputed for a new relief, and its topology untouched.
 *
 * `heightScale` reaches the geometry in exactly one place - the scaled copy the normals are taken
 * against - so a relief drag was re-running the seeding, the refinement loop, every Delaunay pass
 * and the packing to arrive at bit-identical positions, values and indices, then destroying and
 * re-uploading both GPU vertex buffers on top. This is the part that can actually change.
 *
 * One path covers both shadings. On a welded mesh `computeNormals` averages the faces meeting at
 * each vertex; on a faceted one every vertex belongs to a single triangle, so the average is that
 * triangle's own normal - which is the flat normal {@link toFacetedMesh} writes, by the same
 * winding rule.
 */
export const rescaleNormals = (
  mesh: SurfaceMesh,
  heightScale: number
): SurfaceMesh => {
  if (mesh.vertexCount === 0) return mesh;

  const scaled = Float32Array.from(mesh.positions);
  for (let offset = 2; offset < scaled.length; offset += 3) {
    scaled[offset] *= heightScale;
  }

  return {
    ...mesh,
    normals: computeNormals(scaled, mesh.indices, mesh.vertexCount),
  };
};

/**
 * Every grid cell as two triangles - the mesh the renderer used before adaptive refinement.
 *
 * Cells touching no-data are skipped whole, so a gap in the estate is a hole rather than a facet
 * dragged to the floor. The diagonal is chosen per cell as the one across which the field varies
 * least, which keeps the seam off a ridge running corner to corner.
 */
export const toUniformMesh = (
  grid: ScalarGrid,
  heightScale: number
): SurfaceMesh => {
  if (grid.width < 2 || grid.height < 2) return EMPTY_MESH;

  const vertices: MeshVertex[] = [];
  const indexOf = new Map<number, number>();
  const indices: number[] = [];

  const vertexAt = (column: number, row: number): number | null => {
    const gridIndex = row * grid.width + column;
    const existing = indexOf.get(gridIndex);
    if (existing !== undefined) return existing;

    const value = grid.values[gridIndex];
    if (value === null) return null;

    const index = vertices.length;
    vertices.push({ x: column, y: row, value });
    indexOf.set(gridIndex, index);
    return index;
  };

  for (let row = 0; row < grid.height - 1; row++) {
    for (let column = 0; column < grid.width - 1; column++) {
      const lowLeft = vertexAt(column, row);
      const lowRight = vertexAt(column + 1, row);
      const highRight = vertexAt(column + 1, row + 1);
      const highLeft = vertexAt(column, row + 1);
      if (
        lowLeft === null ||
        lowRight === null ||
        highRight === null ||
        highLeft === null
      ) {
        continue;
      }

      const acrossOne = Math.abs(
        vertices[highRight].value - vertices[lowLeft].value
      );
      const acrossOther = Math.abs(
        vertices[highLeft].value - vertices[lowRight].value
      );

      if (acrossOne <= acrossOther) {
        indices.push(
          lowLeft,
          lowRight,
          highRight,
          lowLeft,
          highRight,
          highLeft
        );
        continue;
      }
      indices.push(lowLeft, lowRight, highLeft, lowRight, highRight, highLeft);
    }
  }

  return packMesh({ vertices, indices, grid, heightScale });
};

/**
 * Rebuilds a mesh with per-face normals and no shared vertices.
 *
 * The third way through the tension between smooth lighting and triangle count. Interpolated
 * normals need a dense mesh to look smooth, and on a coarse one they read as a lumpy
 * approximation of something that should be crisp. Rather than fight that, this embraces it: give
 * every triangle its own three vertices carrying its own face normal, and the surface renders as
 * genuinely flat facets with hard edges between them - the low-poly look, which is a deliberate
 * style rather than a failure to be smooth.
 *
 * Note what this is not: it is not the cheap flat fill, which paints one colour per facet. Every
 * vertex of a triangle carrying the same normal means the rasteriser's interpolation yields that
 * constant normal at every pixel - so the facet is flat-lit, but the halfway vector still varies
 * across it and a specular highlight still travels over the face. Flat geometry, real lighting.
 *
 * Costs three vertices per triangle instead of roughly one, which is the price of not sharing.
 *
 * Takes `heightScale` for the same reason {@link packMesh} does: positions carry z in [0, 1] and
 * leave the exaggeration to the camera, so a face normal read straight off them would describe the
 * unexaggerated surface and the relief dial would stop reaching the shading.
 */
export const toFacetedMesh = (
  mesh: SurfaceMesh,
  heightScale: number
): SurfaceMesh => {
  if (mesh.triangleCount === 0) return EMPTY_MESH;

  const vertexCount = mesh.triangleCount * 3;
  const positions = new Float32Array(vertexCount * 3);
  const normals = new Float32Array(vertexCount * 3);
  const values = new Float32Array(vertexCount);
  const indices = new Uint32Array(vertexCount);
  /* The index each corner was copied from. Two corners sharing one is the same point of the
     surface, which is what the wireframe and the drop lines need to know and what unwelding
     otherwise throws away. */
  const sharedVertices = new Uint32Array(vertexCount);

  for (let triangle = 0; triangle < mesh.triangleCount; triangle++) {
    const source = [
      mesh.indices[triangle * 3],
      mesh.indices[triangle * 3 + 1],
      mesh.indices[triangle * 3 + 2],
    ];

    source.forEach((sourceIndex, corner) => {
      const target = (triangle * 3 + corner) * 3;
      positions[target] = mesh.positions[sourceIndex * 3];
      positions[target + 1] = mesh.positions[sourceIndex * 3 + 1];
      positions[target + 2] = mesh.positions[sourceIndex * 3 + 2];
      values[triangle * 3 + corner] = mesh.values[sourceIndex];
      indices[triangle * 3 + corner] = triangle * 3 + corner;
      sharedVertices[triangle * 3 + corner] = sourceIndex;
    });

    const [a, b, c] = source.map(index => index * 3);
    const e1x = mesh.positions[b] - mesh.positions[a];
    const e1y = mesh.positions[b + 1] - mesh.positions[a + 1];
    const e1z = (mesh.positions[b + 2] - mesh.positions[a + 2]) * heightScale;
    const e2x = mesh.positions[c] - mesh.positions[a];
    const e2y = mesh.positions[c + 1] - mesh.positions[a + 1];
    const e2z = (mesh.positions[c + 2] - mesh.positions[a + 2]) * heightScale;

    let nx = e1y * e2z - e1z * e2y;
    let ny = e1z * e2x - e1x * e2z;
    let nz = e1x * e2y - e1y * e2x;
    if (nz < 0) {
      nx = -nx;
      ny = -ny;
      nz = -nz;
    }
    /* Faced upward when the cross product vanishes, matching `computeNormals`' orphan branch. A
       degenerate facet used to get (0, 0, 0) here and (0, 0, 1) there, so `rescaleNormals` - which
       reaches both shadings through `computeNormals` - would have flipped such a facet from
       ambient-only to lit on a relief change. Unreachable today, since `collapseCoincident` drops
       exact duplicates before triangulation and a wholly collinear field exits as an empty grid,
       but the two paths are documented as agreeing and now do. */
    const length = Math.hypot(nx, ny, nz);
    const [unitX, unitY, unitZ] =
      length === 0 ? [0, 0, 1] : [nx / length, ny / length, nz / length];

    /* The same normal written to all three corners, which is what makes the facet flat: the
       rasteriser interpolates normals, and interpolating three identical vectors is a constant. */
    for (let corner = 0; corner < 3; corner++) {
      const target = (triangle * 3 + corner) * 3;
      normals[target] = unitX;
      normals[target + 1] = unitY;
      normals[target + 2] = unitZ;
    }
  }

  return {
    positions,
    normals,
    values,
    indices,
    sharedVertices,
    vertexCount,
    triangleCount: mesh.triangleCount,
  };
};
